import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link, Redirect } from 'react-router-dom';
import config from '../config';
import ReactMarkdown from 'react-markdown';
import styled from 'styled-components';
import posts from '../assets/blogs';

const BlogPostStyles = styled.div`
  padding: 10rem 0 8rem;
  min-height: 100vh;

  .post__back {
    display: inline-block;
    font-size: 1.4rem;
    color: var(--highlight-blue);
    margin-bottom: 4rem;
    border-bottom: 1px solid transparent;
    transition: border-color 0.2s ease;
    &:hover {
      border-color: var(--highlight-blue);
    }
  }

  .post__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.8rem;
    margin-bottom: 1.8rem;
  }

  .post__tag {
    font-size: 1.1rem;
    background: var(--gray-2);
    color: var(--highlight-blue);
    border-radius: 4px;
    padding: 0.3rem 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.1rem;
  }

  .post__title {
    font-size: 4.2rem;
    font-family: 'Montserrat SemiBold';
    color: var(--gray-1);
    line-height: 1.2;
    margin-bottom: 1.2rem;
  }

  .post__meta {
    font-size: 1.3rem;
    color: var(--gray-1);
    opacity: 0.5;
    margin-bottom: 4rem;
    display: flex;
    gap: 1.5rem;
  }

  .post__divider {
    border: none;
    border-top: 1px solid var(--gray-2);
    margin-bottom: 4rem;
  }

  .post__body {
    max-width: 720px;
    font-size: 1.7rem;
    line-height: 1.85;
    color: var(--gray-1);

    h1, h2, h3, h4 {
      font-family: 'Montserrat SemiBold';
      color: var(--gray-1);
      margin: 3rem 0 1.2rem;
      line-height: 1.3;
    }
    h1 { font-size: 3.2rem; }
    h2 { font-size: 2.6rem; }
    h3 { font-size: 2rem; }

    p {
      margin-bottom: 2rem;
      opacity: 0.88;
    }

    ul, ol {
      padding-left: 2.4rem;
      margin-bottom: 2rem;
      li {
        margin-bottom: 0.6rem;
        opacity: 0.88;
        list-style: disc;
      }
    }

    strong {
      color: var(--gray-1);
      opacity: 1;
    }

    a {
      color: var(--highlight-blue);
      border-bottom: 1px solid transparent;
      transition: border-color 0.2s ease;
      &:hover {
        border-color: var(--highlight-blue);
      }
    }

    code {
      background: var(--gray-2);
      padding: 0.2rem 0.6rem;
      border-radius: 4px;
      font-size: 1.4rem;
      font-family: 'RobotoMono Regular';
    }

    pre {
      background: var(--deep-dark);
      border: 1px solid var(--gray-2);
      border-radius: 8px;
      padding: 2rem;
      overflow-x: auto;
      margin-bottom: 2rem;
      code {
        background: none;
        padding: 0;
        font-size: 1.4rem;
      }
    }

    blockquote {
      border-left: 3px solid var(--highlight-blue);
      padding-left: 2rem;
      margin: 2rem 0;
      opacity: 0.8;
      font-style: italic;
    }

    hr {
      border: none;
      border-top: 1px solid var(--gray-2);
      margin: 3rem 0;
    }
  }

  .post__not-found {
    font-size: 1.8rem;
    opacity: 0.6;
    text-align: center;
    margin-top: 6rem;
  }

  @media only screen and (max-width: 768px) {
    padding: 8rem 0 4rem;
    .post__title {
      font-size: 2.8rem;
    }
    .post__body {
      font-size: 1.5rem;
    }
  }
`;

export default function BlogPost() {
  const { slug } = useParams();

  if (!config.blogEnabled) return <Redirect to="/" />;
  const post = posts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <BlogPostStyles>
        <div className="container">
          <Link className="post__back" to="/blog">← Back to Blog</Link>
          <p className="post__not-found">Post not found.</p>
        </div>
      </BlogPostStyles>
    );
  }

  return (
    <BlogPostStyles>
      <Helmet><title>{post.title} | Anas Qazi</title></Helmet>
      <div className="container">
        <Link className="post__back" to="/blog">← Back to Blog</Link>

        <div className="post__tags">
          {post.tags.map((tag) => (
            <span className="post__tag" key={tag}>{tag}</span>
          ))}
        </div>

        <h1 className="post__title">{post.title}</h1>

        <div className="post__meta">
          <span>{post.date}</span>
          <span>{post.readTime}</span>
        </div>

        <hr className="post__divider" />

        <div className="post__body">
          <ReactMarkdown>{post.content}</ReactMarkdown>
        </div>
      </div>
    </BlogPostStyles>
  );
}
